import React from 'react';
import { Link } from 'react-router-dom';
import PdfToWord from '../components/PdfToWord';
import Seo from '../components/seo/Seo';
import { softwareAppSchema, howToSchema, breadcrumbSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const PATH = '/pdf-to-word';
const TITLE = 'PDF to Word Converter — Extract Text to .docx';
const DESCRIPTION = 'Extract text from a PDF into an editable .docx Word document — in your browser, without uploading the file. Free, private, honest about limitations.';

const PdfToWordPage = () => (
    <>
        <Seo
            title={TITLE}
            description={DESCRIPTION}
            path={PATH}
            jsonLd={[
                softwareAppSchema({ name: 'PDF to Word Converter', description: DESCRIPTION, path: PATH }),
                howToSchema({
                    name: 'How to convert a PDF to Word',
                    description: 'Extract the text from a PDF into an editable .docx Word document in the browser.',
                    steps: [
                        'Pick a PDF from your device.',
                        'Click "Convert to Word".',
                        'Watch the per-page progress bar.',
                        'A .docx file downloads automatically. Open it in Word or Google Docs and format as needed.',
                    ],
                }),
                breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'PDF to Word', path: PATH },
                ]),
            ]}
        />

        <div className="page-container">
            <article>
                <header className="page-header">
                    <h1>PDF to Word Converter</h1>
                    <p className="page-lead">
                        Pull the text out of a PDF and save it as an editable Word (.docx)
                        document. Great for re-using content; honest about what's hard to
                        preserve.
                    </p>
                </header>

                <section className="tool-embed">
                    <PdfToWord />
                </section>

                <GoogleAd slot={AD_SLOTS.PDF_TO_WORD} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>What this tool is good at</h2>
                    <p>
                        If your goal is to grab the text content of a PDF so you can edit,
                        rewrite, or paraphrase it, this tool is a fast, private way to do that.
                        You'll get a <code>.docx</code> you can open in Microsoft Word, Google
                        Docs, LibreOffice, or Pages — ready for editing.
                    </p>

                    <h2>What this tool is honestly not good at</h2>
                    <p>
                        We're not going to oversell this. PDF-to-Word is one of the hardest
                        round-trip conversions in document computing, because PDF was designed as
                        a <em>visual</em> format, not a <em>structural</em> one. A PDF doesn't
                        natively know what a paragraph is, what a heading is, or where a table
                        starts. Any converter has to guess — and even paid desktop tools get it
                        wrong on complex documents.
                    </p>
                    <p>
                        Specifically, this client-side tool:
                    </p>
                    <ul>
                        <li>
                            <strong>Preserves raw text</strong> page-by-page, in roughly the
                            order the PDF exposes it.
                        </li>
                        <li>
                            <strong>Does not preserve</strong> complex column layouts (newspapers,
                            magazines, academic papers with sidebars).
                        </li>
                        <li>
                            <strong>Does not rebuild tables</strong> into real Word tables.
                            Tabular data is dumped as linear text.
                        </li>
                        <li>
                            <strong>Does not extract images.</strong> If you need images from a
                            PDF, use <Link to="/pdf-to-image">PDF to Image</Link>.
                        </li>
                        <li>
                            <strong>Does not run OCR.</strong> If your PDF is a scanned document
                            (image-only, no embedded text layer), there's nothing to extract.
                            You'll need an OCR tool first.
                        </li>
                    </ul>

                    <h2>Is the PDF scanned or digital?</h2>
                    <p>
                        Open your PDF in any viewer and try selecting a word with your cursor.
                        If your cursor highlights individual words, the PDF has a text layer and
                        this tool will work. If your cursor only drags a rectangle (like over an
                        image), the PDF is a scan — this tool will produce a blank Word document.
                        Scans need OCR first, which is best handled by a desktop tool or a
                        specialized OCR service.
                    </p>

                    <h2>How to use the tool</h2>
                    <ol>
                        <li>Pick a PDF from your device.</li>
                        <li>Click <strong>Convert to Word</strong>.</li>
                        <li>Watch the per-page progress bar.</li>
                        <li>A <code>.docx</code> file downloads automatically.</li>
                        <li>Open it in Word or Google Docs and format as needed.</li>
                    </ol>

                    <h2>Tips for getting cleaner output</h2>
                    <ul>
                        <li>
                            <strong>Start with the cleanest PDF available.</strong> If you have
                            access to the original Word document, use that — any PDF-to-Word tool
                            is a lossy guess.
                        </li>
                        <li>
                            <strong>Strip a table-heavy PDF down to the text you actually need.</strong>
                            Delete the extraneous pages in a PDF viewer before converting.
                        </li>
                        <li>
                            <strong>Expect to do some cleanup.</strong> Even the best converters
                            produce output that needs a human pass to restore headings, fix
                            broken paragraphs, and reapply styles.
                        </li>
                    </ul>

                    <h2>Why run this in the browser?</h2>
                    <p>
                        Academic papers, contracts, and internal reports are exactly the kind of
                        documents where you'd least like to upload a copy to a random server. A
                        browser-based converter keeps your content local. If the output isn't
                        good enough for your use case, you've lost only a minute — you haven't
                        handed over a copy of a sensitive document to a third party.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Related tools</h2>
                    <ul>
                        <li><Link to="/word-to-pdf">Word to PDF</Link> — reverse direction.</li>
                        <li><Link to="/pdf-to-image">PDF to Image</Link> — export pages as PNG.</li>
                        <li><Link to="/compress-pdf">Compress PDF</Link>.</li>
                    </ul>
                </section>
            </article>
        </div>
    </>
);

export default PdfToWordPage;
